/**
 * State 6 of 9 — partial data (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by:** the completeness the backend already computes and already refuses
 * to collapse —
 *
 *   `AttendanceDay.completeness` in `openapi.yaml`: `score` (0–1),
 *   `missing_agent_data`, `unsynced_data`, `device_count`. API_CONTRACT §8.3 puts it
 *   plainly: "Con số attendance không kèm completeness là con số nói dối" — an
 *   attendance figure without its completeness is a figure that lies.
 *
 *   `WorkSession.completeness_status` ∈ `complete` | `partial` | `missing`
 *   (`src/modules/attendance/sessions.ts`), and the aggregate `completeness` of
 *   `src/modules/aggregates/aggregate.ts`. `partial` and `missing` are distinct
 *   there and must stay distinct here.
 *
 * It feeds the productivity score's `data_completeness` and `confidence` (spec 01
 * §11), which is why `score` is rendered as a percentage rather than hidden: a
 * manager comparing two people needs to see that one of the two numbers is built on
 * 60 % of the evidence.
 *
 * `excludes` is required and is prose, because the useful sentence is not "data is
 * partial" but *which part is missing and what the figure therefore leaves out*.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import { StatusNote } from './StatusNote';

export interface PartialDataProps {
  /** What the displayed figure does not include. Screen-specific by design. */
  readonly excludes: string;
  /** `completeness.score`, 0–1. Rendered as a percentage; `null` when unknown. */
  readonly score?: number | null;
  /** `completeness.device_count` — how many devices contributed. */
  readonly deviceCount?: number | null;
  readonly actions?: ReactNode;
  readonly testId?: string;
}

export function PartialData({
  excludes,
  score = null,
  deviceCount = null,
  actions,
  testId = 'state-partial-data',
}: PartialDataProps): ReactNode {
  const t = useT();
  return (
    <StatusNote
      severity="medium"
      title={t('state.partialData.title')}
      testId={testId}
      actions={actions}
    >
      <p>{t('state.partialData.body', { excludes })}</p>
      {score === null ? null : (
        <p className="wp-small wp-muted">
          {t('state.partialData.score', { percent: Math.round(score * 100) })}
        </p>
      )}
      {deviceCount === null ? null : (
        <p className="wp-small wp-muted">
          {t('state.partialData.devices', { count: deviceCount })}
        </p>
      )}
    </StatusNote>
  );
}
